import { en } from "./locales/en.js";
import { zhCN } from "./locales/zh-CN.js";
import type { Language } from "./types.js";

/**
 * The language router: the single place that knows which languages this app has.
 *
 * ----------------------------------------------------------------------------
 * Adding a language (the whole procedure):
 *   1. copy `locales/en.ts` to `locales/<code>.ts` and type it as `Messages`
 *      (TypeScript then lists every key that is still missing);
 *   2. add one row to `LANGUAGES` below.
 * Nothing else changes: the picker, negotiation, storage, `Intl` formatting and
 * `<html lang>` all read this array.
 * ----------------------------------------------------------------------------
 *
 * It also owns the *current* language as a small subscribable store rather than
 * React state, because route loaders and actions run outside the component tree
 * and still need to produce translated text.
 */
export const LANGUAGES = [
  { code: "en", tag: "en", label: "English", nativeLabel: "English", dir: "ltr", messages: en },
  { code: "zh-CN", tag: "zh-CN", label: "Chinese (Simplified)", nativeLabel: "简体中文", dir: "ltr", messages: zhCN },
] as const satisfies readonly Language[];

/** Code of a language this app ships. */
export type LocaleCode = (typeof LANGUAGES)[number]["code"];

/** Used when nothing matches, and as the fallback for a key a catalog is missing. */
export const DEFAULT_LOCALE: LocaleCode = "en";

const STORAGE_KEY = "diotia.language";

export function isLocale(value: unknown): value is LocaleCode {
  return typeof value === "string" && LANGUAGES.some((l) => l.code === value);
}

/** The descriptor for a code; falls back to the default language. */
export function languageFor(code: LocaleCode): Language {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0];
}

/**
 * Picks the best available language for a list of preferences (`navigator.languages`,
 * an `Accept-Language` order, …). Exact tag first, then the primary subtag, so
 * `zh-Hans-CN` and `zh` both land on `zh-CN` while there is only one Chinese catalog.
 */
export function matchLocale(preferred: readonly string[]): LocaleCode | null {
  for (const raw of preferred) {
    const want = raw.toLowerCase();
    const exact = LANGUAGES.find((l) => l.code.toLowerCase() === want || l.tag.toLowerCase() === want);
    if (exact) return exact.code;
    const primary = want.split("-")[0] ?? want;
    const base = LANGUAGES.find((l) => (l.code.toLowerCase().split("-")[0] ?? l.code) === primary);
    if (base) return base.code;
  }
  return null;
}

function readStored(): LocaleCode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null; // private mode or blocked site data
  }
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

/** An explicit choice wins over what the browser asks for, which wins over the default. */
export function resolveInitialLocale(
  input: { stored?: LocaleCode | null; preferred?: readonly string[] } = {},
): LocaleCode {
  const stored = input.stored === undefined ? readStored() : input.stored;
  if (stored) return stored;
  return matchLocale(input.preferred ?? browserLanguages()) ?? DEFAULT_LOCALE;
}

let current: LocaleCode | null = null;
const listeners = new Set<() => void>();

export function getLocale(): LocaleCode {
  if (current === null) current = resolveInitialLocale();
  return current;
}

export function getLanguage(): Language {
  return languageFor(getLocale());
}

export function getMessages(): Language["messages"] {
  return getLanguage().messages;
}

/** Mirrors the active language onto the document so the browser reads and renders it correctly. */
export function applyDocumentLanguage(): void {
  if (typeof document === "undefined") return;
  const language = getLanguage();
  document.documentElement.lang = language.tag;
  document.documentElement.dir = language.dir;
}

export function setLocale(next: LocaleCode): void {
  const code = isLocale(next) ? next : DEFAULT_LOCALE;
  if (code === getLocale()) return;
  current = code;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* per-viewer convenience only */
  }
  applyDocumentLanguage();
  for (const listener of listeners) listener();
}

/** Subscribe to language changes. Returns the unsubscribe function (`useSyncExternalStore` shape). */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
